import { Avatar, Stack, Tooltip, Typography } from "@mui/material";
import { useMemberInfo } from "../../hooks/use-member-info";
import {
  type ArgusUserInfo,
  getSenderDisplayName,
  getSenderInitials,
} from "../../lib/argus/argus-user";

export interface ArgusSenderChipProps {
  sender: ArgusUserInfo;
  /**
   * When true, the chip is rendered with reduced opacity — used for the
   * signed-in user's own messages where the identity is already implicit
   * in the bubble alignment.
   */
  isSelf?: boolean;
}

/**
 * Small avatar + name chip rendered above a user message bubble so humans
 * in the same org-shared session can see which teammate sent it. Sourced
 * from the gateway's per-message sender block (see
 * `lib/argus/argus-user.ts::parseSenderBlock`).
 *
 * For Clerk senders we resolve the live profile image via `useMemberInfo()`
 * — same path `CreatedByColumn` uses. Non-Clerk sources (future: slack,
 * cron) fall back to initials until per-source avatar lookups exist.
 */
export const ArgusSenderChip = ({ sender, isSelf = false }: ArgusSenderChipProps) => {
  const { getMember } = useMemberInfo();
  // Only Clerk senders can be resolved via the org membership list; other
  // sources hand us no imageUrl, so skip the lookup to avoid a misleading
  // "User ID: …" fallback tooltip.
  const clerkMember = sender.source === "clerk" ? getMember(sender.id) : null;
  const imageUrl = clerkMember?.imageUrl;

  const displayName = getSenderDisplayName(sender);
  const initials = getSenderInitials(sender);

  // Tooltip: always show email when we have one (the useful disambiguator
  // in a multi-user chat). Only surface `source:id` for non-Clerk senders
  // where it's the only stable identifier.
  const tooltipLines = [
    sender.email,
    sender.source !== "clerk" ? `${sender.source}:${sender.id}` : null,
  ].filter((line): line is string => typeof line === "string" && line.length > 0);
  const tooltipTitle = tooltipLines.length > 0 ? tooltipLines.join("\n") : displayName;

  return (
    <Tooltip
      title={<span style={{ whiteSpace: "pre-line" }}>{tooltipTitle}</span>}
      placement="top"
      arrow
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        sx={{
          // Follow the bubble alignment: self messages render on the right
          // (flex-end), other-user messages render on the left (flex-start).
          // Hard-coding flex-end here silently overrode the parent column's
          // `alignItems` for other-user bubbles.
          alignSelf: isSelf ? "flex-end" : "flex-start",
          opacity: isSelf ? 0.55 : 1,
          marginBottom: 0.5,
        }}
      >
        <Avatar
          src={imageUrl}
          alt={displayName}
          sx={{
            width: 20,
            height: 20,
            fontSize: "0.625rem",
            fontWeight: 600,
            bgcolor: isSelf ? "grey.500" : "primary.main",
            color: "common.white",
          }}
        >
          {initials}
        </Avatar>
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.75rem",
            color: "text.secondary",
            lineHeight: 1,
          }}
        >
          {displayName}
        </Typography>
      </Stack>
    </Tooltip>
  );
};
